// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useAtividades.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { atividadeService } from '../services/atividadeService';
import type {
  AtividadeFiltros, CriarAtividadeRequest, AtualizarAtividadeRequest,
} from '../types';

/** Lista paginada no servidor (filtros, período, busca e ordem em `filtros`). */
export function useAtividades(filtros: AtividadeFiltros = {}, pagina = 1, tamanhoPagina = 20) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['atividades', 'lista', pagina, tamanhoPagina, filtros],
    queryFn: () => atividadeService.getPaginado(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['atividades'] });

  const create = useMutation({
    mutationFn: (req: CriarAtividadeRequest) => atividadeService.create(req),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, req }: { id: number; req: AtualizarAtividadeRequest }) =>
      atividadeService.update(id, req),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => atividadeService.delete(id),
    onSuccess: invalidate,
  });

  return { query, create, update, remove };
}
